import { ERROR, SUCCESS } from "../../config/action-states";
import { withCachedResponse } from "../../helpers/cached-request";
import { withExponentialBackoffRetry } from "../../helpers/function";
import { isServerDownError, HTTP_STATUS_CODES as SERVER_STATUS_CODES  } from "../../helpers/http";
import { 
  deleteDefaultCartId, 
  getDefaultCartId, 
  setDefaultCartId as setAsDefaultCartId 
} from "../../helpers/cart";
import logger from "../../helpers/logger";
import { parseProductForDisplay, parseProductVariantForDisplay } from "../../helpers/product";
import { ApiResponse,  IErrorResponse,  ISuccessResponse, Cart, CartItem as ICartItem } from "../../interfaces";
import type { Product, ProductType, ProductVariant } from "../../interfaces/product";
import CartService from "../../services/cart-service";
import ProductService from "../../services/product-service";
import createStore from "../create-store";

const [useCart, setCart] = createStore<Cart | null>(null);
//const [useCartOperationInProgress, setCartOperationInProgress] = createStore<boolean>(false);

type APICartResponse = {
  id: string;
  owner: string;
  status: string;
  items: Array<{
    item_id: string;
    item_type: ProductType;
    quantity: number; 
    price: number;
    discounted_price: number; 
    currency_code: string;
  }>;
}

const getCartCached = withCachedResponse(CartService.getCart, { 
  cacheKey: process.env.CART_NAME ?? "shopping-cart",
  expiresIn: Number(process.env.CART_EXPIRY) || 300,
  isSuccessfulRequest: (response) => SUCCESS in response,
});

(async function() {
  await (withExponentialBackoffRetry(createDefaultCartIfNotExists)());
}());

async function createDefaultCartIfNotExists() { 
  let defaultCartId = getDefaultCartId();

  async function createNewCart() {
    const response = await CartService.createCart();

    if(SUCCESS in response) {
      const data = response.data as unknown as { cart: Cart };
      const cart = data.cart as unknown as APICartResponse;
        
      return cart;
    }

    return null;
  }

  async function createAndSetDefaultCart() {
    const cart = await createNewCart();

    if(cart) { 
      const cartId = cart.id;
  
      defaultCartId = cartId;
  
      setAsDefaultCartId(cartId);
    }

    return cart;
  }

  try {
    if(!defaultCartId) {
      await createAndSetDefaultCart();
    }

    const response = await getCartCached(defaultCartId);

    if(response && SUCCESS in response) {
      const apiCart = response.data.cart as unknown as APICartResponse;
      const cart = parseApiCart(apiCart);

      if(cart) {
        setCart(cart);
      }
    } else if(response && ERROR in response) {
      if(response.statusCode === SERVER_STATUS_CODES.NOT_FOUND) {
        deleteDefaultCartId();

        // Clear the previously cached cart
        // so that when the next fetchCart call is made,
        // it sees the updated cart info and not the cached version.
        /* @ts-expect-error: don't check for TS error for the .clearCache */
        getCartCached.clearCache();

        const cart = await createAndSetDefaultCart();

        if(cart) {
          setCart(parseApiCart(cart));
        }
      }
    }
  } catch(error) {
    logger.error(
      "store::cart::createDefaultCartIfNotExists:: Error creating cart",
      error
    );

    if(isServerDownError(error)) {
      throw error;
    }
  }
}

function handleSuccessResponse(response: ApiResponse) {
  const data = (response as ISuccessResponse).data as unknown as { cart: APICartResponse };

  // Clear the previously cached cart
  // so that when the next fetchCart call is made,
  // it sees the updated cart info and not the cached version.
  /* @ts-expect-error: don't check for TS error for the .clearCache */
  getCartCached.clearCache();
  setCart(parseApiCart(data.cart));
}

function handleErrorResponse(response: ApiResponse, options: { method: string }) {
  logger.error(
    `store::cart::${options.method}: API returned error response:`,
    (response as IErrorResponse).errorData.message
  );
}

function onErrorThrown(error, options: { action: string; method: string }) {
  logger.error(`store::cart::${options.method}: Error ${options.action}`, error);

  return { 
    error: true,
    statusCode: error.status,
    errorData: {
      message: error.message
    }
  };
}

async function emptyCart(cartId: string): Promise<ApiResponse> {
  try {
    const response = await CartService.emptyCart(cartId); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "emptyCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `emptying cart ${cartId}`, 
      method: "emptyCart" 
    });
  } finally {
    await fetchCart(cartId, true);
  }
}

async function updateCart(cartId: string, updateData: { ownerId?: string, status?: string }): Promise<ApiResponse> {
  try {
    const response = await CartService.updateCart(cartId, updateData); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "updateCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `updating cart ${cartId} with data ${updateData}`, 
      method: "updateCart" 
    });
  }
}

async function addItemToCart(cartId: string, item: ICartItem): Promise<ApiResponse> {
  try {
    const response = await CartService.addItemToCart(cartId, item); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "addItemToCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `adding item ${item} to cart ${cartId}`, 
      method: "addItemToCart" 
    });
  }
}

async function addItemsToCart(cartId: string, items: ICartItem[]): Promise<ApiResponse> {
  try {
    const response = await CartService.addItemsToCart(cartId, items); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "addItemsToCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `adding items ${items} to cart ${cartId}`, 
      method: "addItemsToCart" 
    });
  }
}

async function removeItemFromCart(cartId: string, itemId: string): Promise<ApiResponse> {
  try {
    const response = await CartService.removeItemFromCart(cartId, itemId); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "removeItemFromCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `removing item ${itemId} from cart ${cartId}`, 
      method: "removeItemFromCart" 
    });
  }
}

async function updateItemInCart(cartId: string, itemId: string, updateData: ICartItem): Promise<ApiResponse> {
  try {
    const response = await CartService.updateItemInCart(cartId, itemId, updateData); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "updateItemInCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `updating item ${itemId} in cart ${cartId}`, 
      method: "updateItemInCart" 
    });
  }
}

async function incrementItemQuantityInCart(cartId: string, itemId: string, incrementNumber: number): Promise<ApiResponse> {
  try {
    const response = await CartService.incrementItemQuantityInCart(cartId, itemId, incrementNumber); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "incrementItemQuantityInCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `incrementing item ${itemId} quantity in cart ${cartId}`, 
      method: "incrementItemQuantityInCart" 
    });
  }
}

async function decrementItemQuantityInCart(cartId: string, itemId: string, decrementNumber: number): Promise<ApiResponse> {
  try {
    const response = await CartService.decrementItemQuantityInCart(cartId, itemId, decrementNumber); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "decrementItemQuantityInCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `decrementing item ${itemId} quantity in cart ${cartId}`, 
      method: "decrementItemQuantityInCart" 
    });
  }
}

async function changeItemQuantityInCart(cartId: string, itemId: string, quantity: number): Promise<ApiResponse> {
  try {
    const response = await CartService.changeItemQuantityInCart(cartId, itemId, quantity); 

    if(SUCCESS in response) {
      handleSuccessResponse(response);
    } else if(ERROR in response) {
      handleErrorResponse(response, { method: "changeItemQuantityInCart" });
    }

    return response;
  } catch(error) {
    return onErrorThrown(error, { 
      action: `changing item ${itemId} quantity in cart ${cartId}`, 
      method: "changeItemQuantityInCart" 
    });
  }
}

/**
 * Caches the item (product(variant)) that it fetches for 300 seconds
 */
async function fetchItemDetails(item: { itemId: string, itemType: string }) {
  const { itemId, itemType } = item;
  let result: Product | ProductVariant | null = null;
  const collection = "product";
  const method = withCachedResponse(ProductService.getProduct, { 
    cacheKey: `${itemType}_${itemId}`, 
    expiresIn: 300 
  });
  

  try {
    const response = await method(itemId, {
      relatedFields: "category, images, inventory"
    });

    if(!response || !(SUCCESS in response)) {
      return result;
    }

    const data = response.data as unknown as object;

    if(data) {
      const object = data[collection];
      result = object;
    }
  } catch(error) {
    logger.error(
      `store::cart::getItemDetails:: Error fetching ${itemType} details with id ${itemId}`,
      error
    );
  }

  if(itemType === "ProductVariant") {
    const variant = result as unknown as  ProductVariant;
    return parseProductVariantForDisplay(variant, variant?.product)
  } else {
    const product = result as Product;
    return parseProductForDisplay(product);
  }
}

export {
  useCart,
  emptyCart,
  updateCart,
  addItemToCart,
  addItemsToCart,
  removeItemFromCart,
  updateItemInCart,
  incrementItemQuantityInCart,
  decrementItemQuantityInCart,
  changeItemQuantityInCart,
  fetchItemDetails
};


// Helpers
async function fetchCart(cartId: string, setCartInState?: boolean): Promise<Cart|null> {
  try {
    const response = await CartService.getCart(cartId);

    if(response && SUCCESS in response) {
      const apiCart = response.data.cart as unknown as APICartResponse;
      const cart = parseApiCart(apiCart);

      if(setCartInState) {
        setCart(cart);
      }

      return cart;
    } else {
      return null;
    }
  } catch(error) {
    logger.error(
      `store::cart::fetchCart:: Error fetching cart ${cartId}`,
      error
    );

    return null;
  }
}

function parseApiCart(apiCart: APICartResponse): Cart {
  return {
    ...apiCart, 
    items: apiCart.items.map(item => ({
      id              : item.item_id,
      type            : item.item_type, 
      quantity        : item.quantity,  
      price           : item.price,
      discountedPrice : item.discounted_price, 
      currencyCode    : item.currency_code
    })),
  }
}
